import isEmpty from 'lodash/isEmpty';
import uniqBy from 'lodash/uniqBy';
import { GetServerSideProps, NextPage } from 'next';
import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { NextRouter, useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';
import styled from 'styled-components';
// import { EntityId } from '@reduxjs/toolkit';
import { useRef, useState } from 'react';
import { APP_URL, IS_NO_ROBOT_INDEX, SHOW_COMMEMORATE } from 'src/constants';
import { IMAGE_DEFAULT, WEB_DESC, WEB_KEYWORD, WEB_NAME, WEB_TITLE, WEB_URL } from 'src/constants/layout';
import { GET } from 'src/services';
import { escapeChar, timestamp } from 'src/utils/helper';

import PageError from 'pages/404';
import DynamicAds, { KEY_ADS_POSITION } from 'src/components/ads/dynamicAds';
import { IMAGE_SIZE } from 'src/components/cards/image';
import SectionAffiliate from 'src/components/sections/section-events/section-affiliate';
import WidgetBreadcrumb from 'src/components/widgets/widget-breadcrumb';
import { IArticle, IMeta, IMetaData } from 'src/interface/section';
import { IAds, IAffiliate, IResponseData } from 'src/interface/service';
import { dataAction } from 'src/redux/data';
import { useAppDispatch, useAppSelector } from 'src/redux/store';
import { decryptionClient } from 'src/services/member-auth/utils/decryption-payload';
// import { KEY_ADS_POSITION } from 'src/utils/ads/dynamic-ads';
import { DATALAYER_TYPE } from 'src/utils/datalayer/send-datalayer';
// import { useMobile } from 'src/utils/devices';
import { isShouldFetchByline } from 'src/utils/detect-scrolling';
import { useMobilePhone } from 'src/utils/device';
import { GET_ARTICLES } from 'src/utils/service';
import useUser from 'src/utils/useUser';

// const WidgetBreadcrumb = dynamic(import('src/components/widgets/widget-breadcrumb-columnists'));
// const SectionAffiliate = dynamic(import('src/components/sections/section-event/section-affiliate'));
// const DynamicAds = dynamic(import('src/components utils/ads/dynamic-ads'));
const Layout = dynamic(import('src/components/layouts'));
// const WidgetMeta = dynamic(import('src/components/widgets/widget-meta'));
const CardV = dynamic(import('src/components/cards/card-v'));
// const CardHImg = dynamic(import('src/components/cards/card-h-img'));
// const InfiniteScroll = dynamic(import('src/components/InfiniteScroll'));
// const SectionHeader = dynamic(import('src/components/sections/section-header'));
const Image = dynamic(import('src/components/cards/image'));
type Props = {
  articles: IArticle[];
  meta: IMeta;
  nextArticles: string;
  ads?: IAds[];
  dataAffiliate: IAffiliate[];
  dataBylines: any;
  tagText: string;
  dataLatestNews: any[];
};

const { clear, initialValue } = dataAction;

const PageColumnistsDetail: NextPage<Props> = ({ articles, meta, nextArticles, ads, dataAffiliate, dataBylines, tagText, dataLatestNews }) => {
  const data: any = useUser();
  const sessionSevId = data?.user ? decryptionClient(data?.user) : null;
  if (isEmpty(articles)) return <PageError sessionSevId={sessionSevId} />;
  const router: NextRouter = useRouter();
  const isDesktop: boolean = useMediaQuery({ query: '(min-width: 481px)' });
  const isMobile = useMobilePhone();
  const _article = useMemo<IMeta>(() => ({ ...meta, title: meta?.title || WEB_TITLE, description: meta?.description || WEB_DESC, keyword: meta?.keyword || WEB_KEYWORD }), [meta]);

  /* ------------------------------- meta ------------------------------- */
  const metaData = useMemo<IMetaData>(
    () => ({
      heading: _article.heading,
      title: _article.subCategory?.th || _article.category?.th || 'Bylines',
      description: _article.description
    }),
    [_article]
  );
  /* ---------------------------- LOADMORE_ARTICLES --------------------------- */
  const handleScroll = async () => {
    if (isShouldFetchByline()) {
      document.removeEventListener('scroll', handleScroll);
      await handleLoad();
    }
  };

  useEffect(() => {
    document.addEventListener('scroll', handleScroll);
    return () => document.removeEventListener('scroll', handleScroll);
  });
  let baseUrlCategory: string = `${APP_URL}/${_article?.category?.en}`;

  /* ------------------------------- Breadcrumb ------------------------------- */
  let setItemListElement: string = '';

  const handleScrollBreadcrumb = (): void => {
    const breadCrumb = document.querySelector('.sticky-breadcrumb') as HTMLElement;
    const header = document.querySelector('.header-wrapper') as HTMLElement;
    if (document.documentElement.scrollTop > 0) {
      const height: number | '' = header?.clientHeight !== undefined ? header?.clientHeight - 1 : '';
      breadCrumb.setAttribute('style', `top: ${height}px; opacity: 1; visibility: visible;`);
    } else {
      breadCrumb.setAttribute('style', 'opacity: 0; visibility: hidden;');
    }
  };

  useEffect(() => {
    document.addEventListener('scroll', handleScrollBreadcrumb);
    return () => document.removeEventListener('scroll', handleScrollBreadcrumb);
  }, []);

  if (_article?.subCategory?.en != null) {
    baseUrlCategory = `${APP_URL}/${_article?.category?.en}/${_article?.subCategory?.en}`;
    setItemListElement = `[
        {
          "@type": "ListItem",
          "position": 1,
          "name": "หน้าแรก",
          "item": "${APP_URL}"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "${_article?.category?.th}",
          "item": "${APP_URL}/${_article?.category?.en}"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": "${_article?.subCategory?.th}",
          "item": "${baseUrlCategory}"
        }
      ]`;
  } else {
    baseUrlCategory = `${APP_URL}/${_article?.category?.en}`;
    setItemListElement = `[
        {
          "@type": "ListItem",
          "position": 1,
          "name": "หน้าแรก",
          "item": "${APP_URL}"
        },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Bylines",
            "item": "${APP_URL}/${_article?.category?.en}"
          }
        ]`;
  }

  const dispatch = useAppDispatch();
  const { ids } = useAppSelector(state => state.data);
  // entities
  // initial value
  useEffect(() => {
    /* -------------------------------- START : LOADMORE -------------------------------- */
    dispatch(initialValue({ pageType: 'category', list: articles, router: router.pathname, nextLink: nextArticles }));
    return () => {
      dispatch(clear());
    };
  }, []);
  const nextLinkRef = useRef(nextArticles);
  const listArticlesRef = useRef(articles);
  const [listArticles, setListArticles] = useState(articles);
  const [show, setShow] = useState(24);
  const [loadData, setLoadData] = useState(false);
  const handleLoad = async () => {
    setLoadData(true);
    try {
      if (nextLinkRef.current) {
        const resData = await GET_ARTICLES(`${nextLinkRef.current || ''}`, false, true);
        // console.log(`${nextLinkRef.current}${convertObjPath({ limit: 20 })}`)
        if (!isEmpty(resData?.data)) {
          nextLinkRef.current = resData?.nextTo || resData?.links?.next;
          // const newLists = [...listArticlesRef.current, ...resData?.data];
          const newLists = [...listArticlesRef.current, ...resData?.data];
          listArticlesRef.current = uniqBy(newLists, 'id');
          setListArticles(listArticlesRef.current);
        }
      }
      setShow(show + 24);
    } catch (err) {
      console.error(`${timestamp()} ==========> LOAD_CATEGORY_LIST ERROR : ${nextLinkRef?.current}`, err);
    }
    setLoadData(false);
  };
  const cloneListArticles = [...listArticles];
  /* ----------------------------------- SEO ---------------------------------- */
  const articleDescriptionSeo: string = escapeChar(_article?.description) || WEB_DESC;

  return (
    <>
      {/* -------------------------------- NEXT_SEO -------------------------------- */}
      <NextSeo
        noindex={IS_NO_ROBOT_INDEX}
        nofollow={IS_NO_ROBOT_INDEX}
        title={_article?.title || WEB_TITLE}
        description={_article?.description || WEB_DESC}
        openGraph={{
          type: 'article',
          locale: 'en_US',
          site_name: WEB_NAME,
          title: _article?.title || WEB_TITLE,
          description: _article?.description || WEB_DESC,
          url: baseUrlCategory,
          images: [
            {
              url: IMAGE_DEFAULT,
              width: 1920,
              height: 1080,
              alt: WEB_NAME
            }
          ]
        }}
        additionalMetaTags={[
          {
            name: 'keywords',
            content: _article?.keyword || WEB_KEYWORD
          }
        ]}
      />
      {/* --------------------------------- HEADER --------------------------------- */}
      <Head>
        <link rel='canonical' href={baseUrlCategory} />
        <link rel='image_src' href={IMAGE_DEFAULT} />

        <meta property='twitter:title' content={_article?.title || WEB_TITLE} />
        <meta property='twitter:description' content={_article?.description || WEB_DESC} />
        <meta property='twitter:image' content={IMAGE_DEFAULT} />

        {/* ------------------------------- Truehit -------------------------------- */}
        <script
          type='text/javascript'
          dangerouslySetInnerHTML={{
            __html: `
                __en_page="${_article?.subCategory?.en ? _article?.subCategory?.en : _article?.category?.en}";
              `
          }}
        />

        {/* ---------------------------- SNIPPET : WebSite --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
                {
                  "@context": "https://schema.org",
                  "@type": "WebSite",
                  "name": "${WEB_NAME}",
                  "url": "${WEB_URL.WEB}",
                  "description": "${articleDescriptionSeo}",
                  "sameAs": [
                    "${WEB_URL.FACEBOOK}",
                    "${WEB_URL.YOUTUBE}",
                    "${WEB_URL.TWITTER}"
                  ],
                  "potentialAction": {
                    "@type": "SearchAction",
                    "target": "${WEB_URL.WEB}search?q={search_term_string}",
                    "query-input": "required name=search_term_string"
                  }
                }
              `
          }}
        />
        {/* ---------------------------- SNIPPET : WebPage --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
                  {
                    "@context": "http://schema.org",
                    "@type": "WebPage",
                    "name": "${_article?.subCategory?.th ? _article?.subCategory.th : _article?.category?.th}",
                    "description": "${articleDescriptionSeo}",
                    "publisher": {
                      "@type": "ProfilePage",
                      "name": "${WEB_NAME}"
                    }
                  }
                `
          }}
        />

        {/* ------------------------ SNIPPET : BreadcrumbList ------------------------ */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
                {
                  "@context": "http://schema.org",
                  "@type": "BreadcrumbList",
                  "itemListElement": ${setItemListElement}
                }
              `
          }}
        />

        {/* <!-- Optimized Very Large Image LCP  --> */}
      </Head>
      {!isEmpty(dataAffiliate) && <SectionAffiliate position={4} data={dataAffiliate} />}
      <Layout sessionSevId={sessionSevId}>
        {!isEmpty(ads) && <DynamicAds position={KEY_ADS_POSITION.BILLBOARD} data={ads} />}
        <PageWrapper className='section-1' style={{ filter: SHOW_COMMEMORATE === 'true' ? 'grayscale(0.7)' : 'unset' }}>
          <div className={`block-left ${!isEmpty(articles) ? '--height' : ''}`}>
            <BreadcrumbWrapper className='sticky-breadcrumb'>
              <WidgetBreadcrumb data={{ section: { th: 'Bylines', en: 'Bylines' }, subCategory: { th: tagText, en: tagText } }} />
            </BreadcrumbWrapper>
            {/* -------------------- Bylines ------------------- */}
            <SectionColumnist>
              <div className='container'>
                <div className='block-1'>
                  <div className='card-item --desktop'>
                    <div className='title line-clamp --2 title-h'>{dataBylines?.p_name}</div>
                    <div
                      className='title line-clamp title-detail'
                      dangerouslySetInnerHTML={{
                        __html: dataBylines?.description
                      }}
                    />
                  </div>
                  <div className='card-item-image'>
                    <div className='card-image'>
                      <Image image={dataBylines?.image ? '/uploads/images/md/' + dataBylines?.image : '/images/default.jpg'} title={dataBylines?.p_name} size={IMAGE_SIZE.LG} />
                    </div>
                  </div>
                </div>
              </div>
            </SectionColumnist>
            {/* -------------------- End Bylines ------------------- */}
            {ids.length < 1 ? (
              <div className='no-article'>{/* <img src={`${IMAGE_DEFAULT}`} alt='no-article' width='100%' /> */}</div>
            ) : (
              <div className='infinite-scroll'>
                {cloneListArticles.slice(0, show).map((item, index) => (
                  <>
                    {isDesktop || index === 0 ? (
                      <CardV
                        data={item}
                        showBlurb={true}
                        key={index}
                        eventDataLayer={{
                          type: DATALAYER_TYPE.TRACK_INDEX,
                          router: router?.pathname,
                          section: 'section-1',
                          data: {
                            title: item?.title,
                            heading: item?.title,
                            index: index + 1
                          }
                        }}
                      />
                    ) : (
                      <CardV
                        data={item}
                        showBlurb={false}
                        key={index}
                        eventDataLayer={{
                          type: DATALAYER_TYPE.TRACK_INDEX,
                          router: router?.pathname,
                          section: 'section-1',
                          data: {
                            title: item?.title,
                            heading: item?.title,
                            index: index + 1
                          }
                        }}
                      />
                    )}
                  </>
                ))}
              </div>
            )}
            {/* {cloneListArticles.length >= show ? <ButtonContainer>{loadData ? <text> Loading ... </text> : <button onClick={handleLoad}> Read More</button>}</ButtonContainer> : ''} */}
          </div>
        </PageWrapper>
        {/* --------------------------------- latest-news --------------------------------- */}
        <SectionWrapper id={'section-2'}>
          <div className='container'>
            <div className='title-left'>
              <a href={'/category/latest-news'} aria-label={'Latest News'} title={'Latest News'}>
                <h2>{'Latest News'}</h2>
              </a>
              <a href={data?.block2?.link} aria-label={data?.block2?.title} title={data?.block2?.title}>
                <p>ดูทั้งหมด</p>
              </a>
            </div>
            <div className='block-1'>
              {isDesktop &&
                dataLatestNews?.slice(0, 4).map((item: IArticle, i: number) => (
                  <div key={i} className='card-item --desktop'>
                    <CardV
                      data={item}
                      showBlurb={false}
                      // ! DATALAYER
                      eventDataLayer={{
                        type: DATALAYER_TYPE.TRACK_INDEX,
                        router: router?.pathname,
                        section: 'section-2',
                        data: {
                          title: item?.title,
                          heading: item?.title,
                          index: i + 1
                        }
                      }}
                    />
                  </div>
                ))}
              {isMobile &&
                dataLatestNews?.slice(0, 4).map((item: IArticle, i: number) =>
                  i === 0 ? (
                    <div className='card-item --mobile' key={i}>
                      <CardV
                        key={i}
                        data={item}
                        showBlurb={false}
                        // ! DATALAYER
                        eventDataLayer={{
                          type: DATALAYER_TYPE.TRACK_INDEX,
                          router: router?.pathname,
                          section: 'section-2',
                          data: {
                            title: item?.title,
                            heading: item?.title,
                            index: i + 1
                          }
                        }}
                      />
                    </div>
                  ) : (
                    <div key={i} className='card-item --mobile'>
                      <CardV
                        showBlurb={false}
                        data={item}
                        key={i}
                        // ! DATALAYER
                        eventDataLayer={{
                          type: DATALAYER_TYPE.TRACK_INDEX,
                          router: router?.pathname,
                          section: 'section-2',
                          data: {
                            title: item?.title,
                            heading: item?.title,
                            index: i + 1
                          }
                        }}
                      />
                    </div>
                  )
                )}
            </div>
          </div>
        </SectionWrapper>
        {!isEmpty(ads) && (
          <div id='postartice' className='ads-sticky-overflow'>
            <DynamicAds position={KEY_ADS_POSITION.POSTARTICLE} data={ads} />
          </div>
        )}
        {/* -------------------- ADS : PPN - AD1X1 ------------------- */}
        {!isEmpty(ads) && <DynamicAds position={KEY_ADS_POSITION.AD1X1} data={ads} />}
      </Layout>
    </>
  );
};

const BreadcrumbWrapper = styled.div`
  display: block;
  position: sticky;
  z-index: 9;
  opacity: 0;
  background-color: white;
  transition: opacity 0.1s ease-in-out;
  visibility: hidden;
`;
const SectionColumnist = styled.div`
  position: relative;
  width: 100%;
  min-width: auto;
  height: auto;
  display: flex;
  flex-direction: column;

  .block-1 {
    background-color: #ffeded;
    flex-direction: row;
    display: flex;
    width: 100%;
    padding: 10px;
    flex-wrap: wrap;
    justify-content: flex-start;
    align-items: center;
    position: relative;
    justify-content: space-between;
    @media (max-width: 425px) {
      padding: 20px;
      justify-content: center;
      align-content: center;
      flex-direction: column-reverse !important;
    }

    .title.line-clamp {
      -webkit-line-clamp: 4;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      overflow: hidden;
      white-space: normal;
    }

    .card-item {
      &.--desktop {
        padding-top: 15px;
        width: 400px;
        //width: 100%;
        min-width: auto;
        height: auto;
        padding-left: 10px;
        padding-right: 10px;

        @media (max-width: 390px) {
          width: 100%;
        }
        @media (max-width: 597px) {
          width: 100%;
        }
      }
      &.--mobile {
        width: 100%;
        padding: 8px 0 8px;
        &:nth-child(5) {
          padding: 8px 0 0;
          .card-h .card-content {
            padding: 8px 0 0;
          }
        }
        .card-h {
          border-top: 1px solid #cc9654;
          border-bottom: none;
          .card-content {
            padding: 8px 0 0;
          }
        }
      }
    }
    .card-image {
      position: relative;
      padding-top: 56.25%;
      width: 150px;
      height: 150px;
      justify-content: center;
      align-content: center;
      display: inline-block;

      .resolution-image {
        border-radius: 5px;
        position: absolute;
        width: 100% !important;
        height: 100% !important;
        top: 0;
        left: 0;
      }
    }
    .card-item-image {
      padding-right: 5px;
      padding-top: 5px;
    }
    img {
      padding: 5px;
      border-radius: 50%;
      width: 100%;
      height: auto;
      object-fit: cover;
      object-position: center;
      aspect-ratio: 16/9;
      transform-origin: 50% 65%;
      transition:
        transform 2s,
        filter 0.5s ease-out;
      /* cls */
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;

      border: 2px solid #f81137;
    }
    /* -------------------------------- CONTENTS -------------------------------- */
    .card-content {
      top: 5px;
      @media (max-width: 767px) {
        margin: 10px 0 2.5px;
      }
    }
    /* ---------------------------------- TITLE --------------------------------- */

    .title {
      position: relative;

      font-size: 32px;
      font-weight: 700;
      color: #000;

      &.title-h {
        text-transform: capitalize;
        &:hover {
          color: red;
        }
      }
      &.title-detail {
        font-size: 12px;
        font-weight: unset;
      }
    }

    @media (max-width: 767px) {
      min-height: 35px;
      line-height: 1.35;
      font-size: 16px;
      display: flex;
      flex-direction: row;
    }
    @media (max-width: 597px) {
      min-height: 35px;
      line-height: 1.35;
      font-size: 16px;
      padding: 20px;
      justify-content: center;
      align-content: center;
      flex-direction: column-reverse !important;
    }
  }
`;
const PageWrapper = styled.section`
  position: relative;
  display: flex;
  flex-direction: row;
  width: 100%;
  max-width: 1300px;
  margin: auto;
  padding: 0px 10px 30px;
  gap: 15px;
  @media (max-width: 768px) {
    gap: 0px;
  }
  /* --------------------------------- BLOCK_1 -------------------------------- */
  .block-right {
    display: flex;
    flex-direction: column;
    width: 35%;
    position: relative;
    .widget-meta {
      //margin-bottom: 30px;
      .meta-description {
        display: none;
      }
    }
    .widget-tags {
      .title {
        font-family: 'Nunito Sans', sans-serif;
        font-size: 28px;
        font-weight: 700;
        letter-spacing: -1px;
      }
      .tags-wrapper {
        max-height: 400px;
        .list-item {
          border: 1px solid #939393;
          background-color: #fff;
          color: #939393;
          font-weight: 600;
          &:hover {
            border: 1px solid #f81137;
            background-color: #f81137;
            color: #fff;
          }
        }
      }
    }

    /* ---------------------------- MOBILE AND TABLET --------------------------- */
    @media (max-width: 1024px) {
      .widget-tags {
        .title {
          font-size: 24px;
        }
        .tags-wrapper {
          max-height: 150px;
          .list-item {
            padding: 5px 8px;
            font-size: 14px;
          }
        }
      }
    }
    @media (max-width: 767px) {
      .widget-tags {
        .title {
          font-size: 18px;
        }
      }
    }
  }

  /* --------------------------------- BLOCK_2 -------------------------------- */
  .block-left {
    &.--height {
      min-height: calc(100vh - 100px);
    }
    width: 100%;
    .infinite-scroll {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      padding-top: 15px;
      .card-v {
        width: 100%;
        min-width: auto;
        max-width: 49%;
        margin: auto;
        .badge {
          position: absolute;
          top: 0px;
          left: 10px;
          width: fit-content;
          background: #f81137;
          box-shadow: 1px 2px 2px rgba(0, 0, 0, 0.2);
          padding: 5px 15px;
          border-radius: 5px;
          small {
            color: #fff;
          }
          @media (max-width: 767px) {
            top: 5px;
          }
        }
        &:nth-child(odd) {
          padding: 3px 0;
          padding-left: 7.5px;
        }
        &:nth-child(even) {
          padding: 3px 0;
          padding-right: 7.5px;
        }
        &:nth-child(1) {
          max-width: 100%;
          margin-bottom: 15px;
          padding-left: 0px;

          .card-footer {
            border: none;
            small {
              font-size: 14px;
            }
          }
          .title {
            margin-bottom: 0;
            /* text-align: center; */
            font-size: 26px;
            /* bottom: 2rem; */
          }
        }
      }

      .card-h-img,
      .card-h {
        padding-bottom: 10px;
        margin-bottom: 10px;
        border-bottom: 1px solid #c4c4c4;
      }
    }
  }
  /* ---------------------------- MOBILE AND TABLET --------------------------- */
  @media (max-width: 1024px) {
    padding-top: 0px;
  }
  @media (max-width: 900px) {
    flex-direction: column-reverse;
    .widget-tags {
      padding: 15px 0 0px;
    }
    .block-right,
    .block-left {
      width: 100%;
      .infinite-scroll {
        .card-v {
          &:not(:nth-child(1)) {
            padding: 3px 0;
            .footer-right {
              /* width: 100%; */
              justify-content: flex-end;
            }
            .title {
              /* bottom: 0.75rem; */
            }
          }
          &:nth-child(odd) {
            padding-left: 5px;
          }
          &:nth-child(even) {
            padding-right: 5px;
          }
          &:nth-child(1) {
            padding-left: 0px !important;
            .title {
              /* text-align: center; */
              font-size: 17px;
              /* bottom: 1rem; */
            }
          }
        }
      }
    }
  }
`;
const ButtonContainer = styled.div`
  text-align: center;

  button {
    width: 95%;
    border: 1px solid #f81137;
    background-color: #e3e3e3;
    color: #555555;
    border-radius: 5px;
    :hover {
      background-color: #f81137;
      color: #fff;
      transition: background-color 1s linear;
    }
  }
`;
const SectionWrapper = styled.section`
  position: relative;
  overflow: hidden;
  display: block;
  width: 100%;
  height: auto;
  padding: 0px 10px 30px;
  .title-left {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
  .section-header {
    h2 {
      text-transform: initial !important;
      border-bottom: 4px solid #f81137;
      font-weight: 700;
      font-size: 46px;
    }
  }

  .block-1 {
    display: flex;
    width: 100%;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-start;
    align-items: center;
    position: relative;
    @media (max-width: 767px) {
      flex-direction: column;
    }
    .card-v {
      .badge {
        position: absolute;
        top: 0px;
        left: 10px;
        width: fit-content;
        background: #f81137;
        box-shadow: 1px 2px 2px rgba(0, 0, 0, 0.2);
        padding: 5px 10px;
        border-radius: 5px;
        small {
          color: #fff;
          font-size: 12px;
        }
        @media (max-width: 767px) {
          top: 5px;
        }
      }
      .title {
        @media (max-width: 767px) {
          min-height: 38px;
        }
      }
    }
    .card-item {
      &.--desktop {
        //width: 33.33%;
        width: 25%;
        min-width: auto;
        height: auto;
        padding: 0 5px;

        @media (max-width: 768px) {
          width: 50%;
        }

        @media (max-width: 320px) {
          width: 100%;
        }
      }
      &.--mobile {
        width: 100%;
        padding: 8px 0 8px;
        &:nth-child(5) {
          padding: 8px 0 0;
          .card-h .card-content {
            padding: 8px 0 0;
          }
        }
        .card-h {
          border-top: 1px solid #f81137;
          border-bottom: none;
          .card-content {
            padding: 8px 0 0;
          }
        }
      }
    }
  }
`;
type TInitProps = {
  statusCode: number;
  errMessage: string | null;
  articles: IArticle[];
  meta: Partial<IMeta>;
  nextArticles: string;
  ads: IAds[];
  dataAffiliate: IAffiliate[];
  tagText: string | null;
  dataBylines: any;
  dataLatestNews: any;
};
export const getServerSideProps: GetServerSideProps = async ({ query, req, res }: any) => {
  const initProps: TInitProps = {
    statusCode: 200,
    errMessage: null,
    articles: [],
    meta: {},
    nextArticles: '',
    ads: [],
    dataAffiliate: [],
    tagText: '',
    dataBylines: {},
    dataLatestNews: {}
  };

  try {
    /* --------------------------------- Bylines --------------------------------- */

    const resData: IResponseData = await GET_ARTICLES(`/api/v1.0/bylines/contents/${query.id}`, false, true);
    const resDataBylines: IResponseData = await GET_ARTICLES(`/api/v1.0/bylines/` + query.id, false, true);
    // if (resData?.statusCode === 404 || isUndefined(resData)) {
    //   res.setHeader('location', '/category/latest-news');
    //   res.statusCode = 301;
    //   res.end();
    // }
    /* --------------------------------- latest-news --------------------------------- */

    const uri: string = '/api/v1.0/categories/latest-news';
    const latestNews: any = await GET(uri, false, false);

    if (!isEmpty(resData?.data)) {
      initProps.nextArticles = resData?.nextTo || resData?.links?.next || '';
      initProps.articles = (resData.data as IArticle[]) || [];
      initProps.tagText = query.id || '';
      initProps.meta = {
        ...resData.meta,
        subCategory: { th: query.id, en: query.id },
        category: { th: 'Bylines', en: 'Bylines' }
      };
    }
    if (!isEmpty(resDataBylines?.data)) {
      initProps.dataBylines = resDataBylines.data;
    }
    if (!isEmpty(latestNews)) {
      initProps.dataLatestNews = latestNews;
    }
  } catch (err: any) {
    console.error(`${timestamp()} ==========> TAGS_LIST ERROR : `, err.message);
    initProps.errMessage = err?.message;
    initProps.statusCode = err?.response?.status || 500;
  }

  return {
    props: initProps
  };
};
export default PageColumnistsDetail;
